import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Building2, CheckCircle, ArrowRight, BookOpen, Settings, Wrench, ClipboardList, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

const GestionCopropriete = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Gestion de Copropriété : Guide Complet 2025 | Tout savoir</title>
        <meta name="description" content="Guide complet sur la gestion de copropriété. Organisation, administration, finances, travaux. Tout ce qu'il faut savoir pour bien gérer votre copropriété." />
        <meta name="keywords" content="gestion copropriété, administration copropriété, syndic copropriété, conseil syndical, gestion immeuble, copropriété en ligne" />
        <link rel="canonical" href="https://copropriete.app/gestion-copropriete" />
      </Helmet>

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">copropriete.app</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>Se connecter</Button>
              <Button onClick={() => window.location.href = '/login'} className="bg-emerald-600 hover:bg-emerald-700 text-white">C'est gratuit</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-cyan-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Guide complet 2025
            </span>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-6">Gestion de Copropriété</h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Tout comprendre sur la gestion d'une copropriété : acteurs, responsabilités, bonnes pratiques.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <main className="prose prose-slate prose-lg max-w-none">
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Home className="w-8 h-8 text-cyan-600" />
              Qu'est-ce que la gestion de copropriété ?
            </h2>
            <p>
              La <strong>gestion de copropriété</strong> englobe l'ensemble des activités nécessaires au bon fonctionnement d'un immeuble en copropriété : administration, entretien, finances, travaux, relations entre copropriétaires.
            </p>
            <p>
              Une bonne gestion garantit :
            </p>
            <ul>
              <li>La conservation et la valorisation du patrimoine</li>
              <li>Le confort des résidents</li>
              <li>La maîtrise des charges</li>
              <li>Le respect de la réglementation</li>
              <li>La prévention des litiges</li>
            </ul>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Settings className="w-8 h-8 text-cyan-600" />
              Les acteurs de la gestion
            </h2>
            
            <h3 className="text-xl font-bold text-slate-800">Le syndicat des copropriétaires</h3>
            <p>
              C'est l'ensemble des copropriétaires réunis en une entité juridique (personne morale). Le syndicat est responsable de la conservation de l'immeuble et de l'administration des parties communes.
            </p>

            <h3 className="text-xl font-bold text-slate-800">Le syndic</h3>
            <p>
              Le syndic est le mandataire du syndicat. Il peut être <strong>professionnel</strong> ou <strong>bénévole</strong> (copropriétaire élu). Ses missions principales :
            </p>
            <ul>
              <li>Exécuter les décisions de l'assemblée générale</li>
              <li>Administrer l'immeuble</li>
              <li>Gérer les finances de la copropriété</li>
              <li>Représenter le syndicat en justice</li>
              <li>Tenir les archives</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-800">Le conseil syndical</h3>
            <p>
              Composé de copropriétaires élus, le conseil syndical assiste et contrôle le syndic. Il est obligatoire sauf si l'AG décide de ne pas en constituer.
            </p>

            <h3 className="text-xl font-bold text-slate-800">L'assemblée générale</h3>
            <p>
              C'est l'organe de décision souverain. Elle se réunit au moins une fois par an pour voter les décisions importantes.
            </p>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <ClipboardList className="w-8 h-8 text-cyan-600" />
              Les domaines de gestion
            </h2>
            
            <h3 className="text-xl font-bold text-slate-800">Gestion administrative</h3>
            <ul>
              <li>Tenue du registre des copropriétaires</li>
              <li>Organisation des assemblées générales</li>
              <li>Rédaction et envoi des procès-verbaux</li>
              <li>Conservation des archives</li>
              <li>Immatriculation au registre national</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-800">Gestion financière</h3>
            <ul>
              <li>Établissement du budget prévisionnel</li>
              <li>Appels de fonds trimestriels</li>
              <li>Suivi des paiements et recouvrement des impayés</li>
              <li>Tenue de la comptabilité</li>
              <li>Présentation des comptes annuels</li>
            </ul>

            <h3 className="text-xl font-bold text-slate-800">Gestion technique</h3>
            <ul>
              <li>Entretien courant de l'immeuble</li>
              <li>Gestion des contrats (ascenseur, chauffage, ménage...)</li>
              <li>Suivi des travaux</li>
              <li>Mise en conformité réglementaire</li>
              <li>Gestion des sinistres</li>
            </ul>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Wrench className="w-8 h-8 text-cyan-600" />
              Syndic professionnel ou bénévole ?
            </h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="px-4 py-3 text-left">Critère</th>
                    <th className="px-4 py-3 text-left">Syndic professionnel</th>
                    <th className="px-4 py-3 text-left">Syndic bénévole</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="px-4 py-3 font-medium">Coût</td>
                    <td className="px-4 py-3">150-300€/lot/an</td>
                    <td className="px-4 py-3">Gratuit ou faible indemnité</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 font-medium">Expertise</td>
                    <td className="px-4 py-3">Professionnel formé</td>
                    <td className="px-4 py-3">Variable selon le bénévole</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 font-medium">Réactivité</td>
                    <td className="px-4 py-3">Variable</td>
                    <td className="px-4 py-3">Souvent meilleure (sur place)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 font-medium">Garanties</td>
                    <td className="px-4 py-3">Obligatoires (RC pro, garantie financière)</td>
                    <td className="px-4 py-3">Non obligatoires</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Adapté pour</td>
                    <td className="px-4 py-3">Grandes copropriétés</td>
                    <td className="px-4 py-3">Petites/moyennes copros (&lt;50 lots)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-cyan-50 border-l-4 border-cyan-500 p-6 rounded-r-xl my-8">
              <p className="text-cyan-900 font-medium m-0">
                💡 <strong>Notre conseil :</strong> Avec les bons outils numériques, le syndic bénévole peut gérer efficacement une copropriété tout en réalisant des économies significatives.
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Les bonnes pratiques de gestion</h2>
            <ul>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" /><strong>Transparence</strong> - Communiquer régulièrement avec les copropriétaires</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" /><strong>Anticipation</strong> - Planifier les travaux et constituer des réserves</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" /><strong>Mise en concurrence</strong> - Comparer les prestataires régulièrement</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" /><strong>Documentation</strong> - Tenir des archives organisées et accessibles</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" /><strong>Suivi rigoureux</strong> - Contrôler les factures et les contrats</li>
              <li><CheckCircle className="inline w-5 h-5 text-emerald-500 mr-2" /><strong>Digitalisation</strong> - Utiliser des outils modernes pour gagner en efficacité</li>
            </ul>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-8 my-8 border border-cyan-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Modernisez votre gestion de copropriété</h3>
            <p className="text-slate-700 mb-6">
              copropriete.app regroupe tous les outils dont vous avez besoin pour une gestion efficace, transparente et économique.
            </p>
            <Button onClick={() => window.location.href = '/login'} size="lg" className="bg-cyan-600 hover:bg-cyan-700">
              Essayer gratuitement pendant 7 jours
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">copropriete.app</span>
          </Link>
          <p className="text-sm">© 2025 copropriete.app. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default GestionCopropriete;
